import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import {
  useGetAdminPayroll,
  useGetAdminDashboard,
  useGetAnalyticsRevenueBreakdown,
  PayrollEntry,
  AdminDashboard,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency, cn } from "@/lib/utils";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { DollarSign, Users, TrendingUp, Activity, Download, Calendar } from "lucide-react";

const PIE_COLORS = ["#e8c848", "#60a5fa", "#34d399", "#f87171", "#a78bfa", "#fb923c"];

type Period = "month" | "3m" | "6m" | "year" | "all";

const PERIODS: { label: string; value: Period }[] = [
  { label: "This Month", value: "month" },
  { label: "3 Months", value: "3m" },
  { label: "6 Months", value: "6m" },
  { label: "This Year", value: "year" },
  { label: "All Time", value: "all" },
];

function getPeriodRange(period: Period): { from?: string; to?: string } {
  const now = new Date();
  const to = now.toISOString();
  if (period === "all") return {};
  if (period === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return { from, to };
  }
  if (period === "3m") {
    const from = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString();
    return { from, to };
  }
  if (period === "6m") {
    const from = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString();
    return { from, to };
  }
  if (period === "year") {
    const from = new Date(now.getFullYear(), 0, 1).toISOString();
    return { from, to };
  }
  return {};
}

function exportCsv(payrollData: PayrollEntry[], byMonth: any[]) {
  const monthRows = byMonth.map(m =>
    [m.month, m.totalRevenue ?? 0, m.gymRevenue ?? 0, m.trainerPayout ?? 0, m.platformFee ?? 0, m.sessionCount ?? 0].join(",")
  );
  const trainerRows = payrollData.map(p =>
    [p.trainerName, p.sessionCount, p.totalRevenue, p.trainerPayout, p.gymRevenue ?? 0].join(",")
  );

  const csvContent = [
    "=== Monthly Revenue Breakdown ===",
    "Month,Total Revenue,Gym Revenue,Trainer Payout,Platform Fee,Sessions",
    ...monthRows,
    "",
    "=== Trainer Payroll ===",
    "Trainer,Sessions,Total Revenue,Trainer Payout,Gym Revenue",
    ...trainerRows,
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `earnings-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminAnalytics() {
  const { user } = useAuth();
  const gymId = user?.gymId ?? "";
  const [period, setPeriod] = useState<Period>("all");

  const range = useMemo(() => getPeriodRange(period), [period]);

  const { data: payroll, isLoading: payrollLoading } = useGetAdminPayroll(
    undefined,
    { query: { retry: false } },
  );
  const { data: dashboard, isLoading: dashLoading } = useGetAdminDashboard(
    { query: { retry: false } },
  );
  const { data: revenueBreakdown, isLoading: revenueLoading } = useGetAnalyticsRevenueBreakdown(
    gymId,
    range,
    { query: { enabled: !!gymId, retry: false } },
  );

  const isLoading = payrollLoading || dashLoading;

  const payrollData: PayrollEntry[] = payroll ?? [];
  const dashboardData: AdminDashboard | undefined = dashboard;

  const totalPayroll = payrollData.reduce((sum, p) => sum + p.trainerPayout, 0);
  const totalRevenue = dashboardData?.totalRevenue ?? 0;
  const gymNet = totalRevenue - totalPayroll;

  const byMonth = (revenueBreakdown as any)?.byMonth ?? [];
  const filteredGymRevenue = (revenueBreakdown as any)?.gymRevenue ?? 0;
  const filteredTrainerPayouts = (revenueBreakdown as any)?.trainerPayouts ?? 0;
  const filteredPlatformFees = (revenueBreakdown as any)?.platformFees ?? 0;
  const filteredTotalRevenue = (revenueBreakdown as any)?.totalRevenue ?? 0;
  const filteredSessions = (revenueBreakdown as any)?.sessionCount ?? 0;

  const stackedData = byMonth.map((m: any) => ({
    month: m.month,
    "Gym Revenue": m.gymRevenue,
    "Trainer Payout": m.trainerPayout,
    "Platform Fee": m.platformFee,
  }));

  const chartData = payrollData.map(p => ({
    name: p.trainerName.split(" ")[0],
    fullName: p.trainerName,
    earnings: p.trainerPayout,
    sessions: p.sessionCount,
  }));

  const pieData = payrollData
    .filter(p => p.trainerPayout > 0)
    .map(p => ({
      name: p.trainerName.split(" ")[0],
      value: p.trainerPayout,
    }));

  const showFiltered = period !== "all";

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Earnings Report</h1>
            <p className="text-muted-foreground text-lg">Revenue, payroll, and performance insights.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-white/10 text-muted-foreground hover:text-white"
            onClick={() => exportCsv(payrollData, byMonth)}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>

        {/* Period Selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex gap-1.5 flex-wrap">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors",
                  period === p.value
                    ? "bg-primary text-black"
                    : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white border border-white/5"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Summary — filtered by period when not All Time */}
        {showFiltered ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><DollarSign className="w-16 h-16 text-primary" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Period Revenue</CardDescription>
                <CardTitle className="text-3xl font-mono text-white">
                  {revenueLoading ? "..." : formatCurrency(filteredTotalRevenue)}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">Completed sessions in range</p></CardContent>
            </Card>

            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><TrendingUp className="w-16 h-16 text-emerald-400" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Gym Revenue</CardDescription>
                <CardTitle className="text-3xl font-mono text-emerald-400">
                  {revenueLoading ? "..." : formatCurrency(filteredGymRevenue)}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">After trainer payouts</p></CardContent>
            </Card>

            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Users className="w-16 h-16 text-blue-400" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Trainer Payouts</CardDescription>
                <CardTitle className="text-3xl font-mono text-blue-400">
                  {revenueLoading ? "..." : formatCurrency(filteredTrainerPayouts)}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">Owed to trainers</p></CardContent>
            </Card>

            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Activity className="w-16 h-16 text-white" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Sessions</CardDescription>
                <CardTitle className="text-3xl font-mono text-white">
                  {revenueLoading ? "..." : filteredSessions}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">Completed in period</p></CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><DollarSign className="w-16 h-16 text-primary" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Revenue</CardDescription>
                <CardTitle className="text-3xl font-mono text-white">
                  {isLoading ? "..." : formatCurrency(totalRevenue)}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">All-time completed sessions</p></CardContent>
            </Card>

            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Users className="w-16 h-16 text-white" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Payroll</CardDescription>
                <CardTitle className="text-3xl font-mono text-white">
                  {isLoading ? "..." : formatCurrency(totalPayroll)}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">Trainer payouts owed</p></CardContent>
            </Card>

            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><TrendingUp className="w-16 h-16 text-emerald-400" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Gym Net Revenue</CardDescription>
                <CardTitle className={cn("text-3xl font-mono", gymNet >= 0 ? "text-emerald-400" : "text-red-400")}>
                  {isLoading ? "..." : formatCurrency(gymNet)}
                </CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-muted-foreground mt-1">After trainer payouts</p></CardContent>
            </Card>

            <Card className="glass-panel border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Activity className="w-16 h-16 text-white" /></div>
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Completed Sessions</CardDescription>
                <CardTitle className="text-3xl font-mono text-white">
                  {isLoading ? "..." : dashboardData?.completedSessions ?? 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mt-1">
                  {isLoading ? "..." : dashboardData?.trainerCount ?? 0} active trainers
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stacked Revenue Breakdown by Month */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Revenue Breakdown by Month</CardTitle>
            <CardDescription>Gym revenue, trainer payouts, and platform fees stacked per month</CardDescription>
          </CardHeader>
          <CardContent>
            {revenueLoading ? (
              <div className="h-[280px] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : stackedData.length === 0 ? (
              <div className="h-[280px] flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Activity className="w-8 h-8 opacity-40" />
                <p>No completed sessions in this period.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-3 h-3 rounded-sm inline-block bg-emerald-400" />Gym Revenue
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-3 h-3 rounded-sm inline-block bg-blue-400" />Trainer Payout
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-3 h-3 rounded-sm inline-block bg-amber-400" />Platform Fee
                  </div>
                </div>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stackedData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                      <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                      <RechartsTooltip
                        cursor={{ fill: "#1e1e24" }}
                        contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                        formatter={(value: number, name: string) => [formatCurrency(value), name]}
                      />
                      <Bar dataKey="Gym Revenue" stackId="a" fill="#34d399" radius={[0, 0, 0, 0]} maxBarSize={56} />
                      <Bar dataKey="Trainer Payout" stackId="a" fill="#60a5fa" radius={[0, 0, 0, 0]} maxBarSize={56} />
                      <Bar dataKey="Platform Fee" stackId="a" fill="#e8c848" radius={[4, 4, 0, 0]} maxBarSize={56} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Platform Fee Summary */}
        {!revenueLoading && (filteredPlatformFees > 0 || !showFiltered) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Gym Keeps</CardDescription>
                <CardTitle className="text-2xl font-mono text-emerald-400">
                  {formatCurrency(showFiltered ? filteredGymRevenue : (gymNet > 0 ? gymNet : 0))}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showFiltered && filteredTotalRevenue > 0 && (
                  <p className="text-xs text-muted-foreground">{Math.round((filteredGymRevenue / filteredTotalRevenue) * 100)}% of gross</p>
                )}
              </CardContent>
            </Card>
            <Card className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Trainer Payouts</CardDescription>
                <CardTitle className="text-2xl font-mono text-blue-400">
                  {formatCurrency(showFiltered ? filteredTrainerPayouts : totalPayroll)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showFiltered && filteredTotalRevenue > 0 && (
                  <p className="text-xs text-muted-foreground">{Math.round((filteredTrainerPayouts / filteredTotalRevenue) * 100)}% of gross</p>
                )}
              </CardContent>
            </Card>
            <Card className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Platform Fees</CardDescription>
                <CardTitle className="text-2xl font-mono text-amber-400">
                  {formatCurrency(showFiltered ? filteredPlatformFees : 0)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showFiltered && filteredTotalRevenue > 0 && (
                  <p className="text-xs text-muted-foreground">{Math.round((filteredPlatformFees / filteredTotalRevenue) * 100)}% of gross</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Trainer Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card className="glass-panel border-white/5 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Trainer Earnings</CardTitle>
              <CardDescription>Total payout per trainer from all completed sessions</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[280px] flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-[280px] flex items-center justify-center text-muted-foreground">No payroll data yet.</div>
              ) : (
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                      <XAxis dataKey="name" stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                      <RechartsTooltip
                        cursor={{ fill: "#1e1e24" }}
                        contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                        itemStyle={{ color: "#e8c848", fontWeight: "bold" }}
                        formatter={(value: number) => [formatCurrency(value), "Payout"]}
                        labelFormatter={(label: string, payload) => payload?.[0]?.payload?.fullName ?? label}
                      />
                      <Bar dataKey="earnings" fill="#e8c848" radius={[4, 4, 0, 0]} maxBarSize={60} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Earnings Share</CardTitle>
              <CardDescription>Payout distribution by trainer</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[280px] flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : pieData.length === 0 ? (
                <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm text-center">No earnings data yet.</div>
              ) : (
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                        {pieData.map((_, idx) => (
                          <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend iconType="circle" iconSize={8} formatter={(value: string) => <span style={{ color: "#a1a1aa", fontSize: 11 }}>{value}</span>} />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                        formatter={(value: number) => [formatCurrency(value), "Payout"]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payroll Table */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Payroll Summary</CardTitle>
            <CardDescription>Session count and full revenue breakdown per trainer</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-14 bg-white/5 rounded-xl" />)}
              </div>
            ) : payrollData.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">No payroll data available yet.</div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-5 px-4 pb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold border-b border-white/5">
                  <span>Trainer</span>
                  <span className="text-right">Sessions</span>
                  <span className="text-right">Gross Revenue</span>
                  <span className="text-right">Gym Revenue</span>
                  <span className="text-right">Trainer Payout</span>
                </div>
                {payrollData.map((entry) => (
                  <div
                    key={entry.trainerId}
                    className="grid grid-cols-5 items-center px-4 py-3 rounded-xl hover:bg-white/[0.03] transition-colors border border-transparent hover:border-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold border border-white/10">
                        {entry.trainerName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)}
                      </div>
                      <span className="text-white font-semibold text-sm">{entry.trainerName}</span>
                    </div>
                    <span className="text-right text-white font-mono">{entry.sessionCount}</span>
                    <span className="text-right text-white font-mono">{formatCurrency(entry.totalRevenue)}</span>
                    <span className="text-right text-emerald-400 font-mono">{formatCurrency((entry as any).gymRevenue ?? 0)}</span>
                    <span className="text-right text-primary font-mono font-bold">{formatCurrency(entry.trainerPayout)}</span>
                  </div>
                ))}
                <div className="grid grid-cols-5 items-center px-4 py-3 mt-2 rounded-xl bg-primary/5 border border-primary/20">
                  <span className="text-white font-bold text-sm">Total</span>
                  <span className="text-right text-white font-mono font-bold">
                    {payrollData.reduce((s, p) => s + p.sessionCount, 0)}
                  </span>
                  <span className="text-right text-white font-mono font-bold">
                    {formatCurrency(payrollData.reduce((s, p) => s + p.totalRevenue, 0))}
                  </span>
                  <span className="text-right text-emerald-400 font-mono font-bold">
                    {formatCurrency(payrollData.reduce((s, p) => s + ((p as any).gymRevenue ?? 0), 0))}
                  </span>
                  <span className="text-right text-primary font-mono font-bold">
                    {formatCurrency(totalPayroll)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
